import type { Rol } from './estados';

/**
 * Catálogo de permisos. Cada rol trae un conjunto por defecto y a cada usuario
 * se le pueden activar extras o revocar permisos individualmente (spec §4).
 */
export const PERMISOS = {
  'agenda.ver': { titulo: 'Ver la agenda completa', grupo: 'Agenda' },
  'agenda.ver_propia': { titulo: 'Ver su propia agenda', grupo: 'Agenda' },
  'agenda.crear': { titulo: 'Agendar citas', grupo: 'Agenda' },
  'agenda.cancelar': { titulo: 'Cancelar citas', grupo: 'Agenda' },
  'agenda.reprogramar': { titulo: 'Reprogramar y reasignar citas', grupo: 'Agenda' },
  'agenda.editar_finalizadas': { titulo: 'Editar citas finalizadas (servicio, barbero, cliente y hora)', grupo: 'Agenda' },
  'agenda.asignar_vacaciones': { titulo: 'Asignar horario a personal con vacaciones', grupo: 'Agenda' },
  'agenda.recibir_citas': { titulo: 'Permitir que se le asignen citas para servicio', grupo: 'Agenda' },
  'clientes.ver': { titulo: 'Ver fichas de clientes', grupo: 'Clientes' },
  'clientes.ver_gasto_total': { titulo: 'Ver el total gastado por cliente', grupo: 'Clientes' },
  'clientes.editar': { titulo: 'Crear y editar clientes', grupo: 'Clientes' },
  'atencion.operar': { titulo: 'Iniciar y finalizar servicios', grupo: 'Atención' },
  'caja.operar': { titulo: 'Abrir caja, hacer cortes y cerrar caja', grupo: 'Caja' },
  'caja.desbloquear': { titulo: 'Desbloquear faltantes de caja', grupo: 'Caja' },
  'ventas.cobrar': { titulo: 'Cobrar ventas', grupo: 'Caja' },
  'ventas.corregir_cobros': { titulo: 'Corregir cobros del día', grupo: 'Caja' },
  'ventas.ver_detalle': { titulo: 'Ver detalle de ventas', grupo: 'Caja' },
  'ventas.devolver_solicitar': { titulo: 'Solicitar devoluciones', grupo: 'Caja' },
  'ventas.devolver_autorizar': { titulo: 'Autorizar devoluciones', grupo: 'Caja' },
  'inventario.ver': { titulo: 'Ver inventario', grupo: 'Inventario' },
  'inventario.editar': { titulo: 'Editar productos, proveedores y compras', grupo: 'Inventario' },
  'inventario.eliminar': { titulo: 'Eliminar productos sin existencias', grupo: 'Inventario' },
  'inventario.margen_cambiar': { titulo: 'Cambiar márgenes de ganancia', grupo: 'Inventario' },
  'personal.ver': { titulo: 'Ver personal y asistencia', grupo: 'Personal' },
  'personal.horarios': { titulo: 'Crear horarios semanales', grupo: 'Personal' },
  'personal.vacaciones_autorizar': { titulo: 'Autorizar vacaciones', grupo: 'Personal' },
  'personal.checar_otros': { titulo: 'Registrar asistencia de otros', grupo: 'Personal' },
  'nomina.ver': { titulo: 'Ver nómina de todos', grupo: 'Nómina' },
  'nomina.propia': { titulo: 'Ver sus comisiones y sueldo', grupo: 'Nómina' },
  'nomina.pagar': { titulo: 'Pagar nómina', grupo: 'Nómina' },
  'boveda.ver': { titulo: 'Ver saldos e historial de la bóveda', grupo: 'Bóveda' },
  'boveda.ingresar': { titulo: 'Ingresar dinero a la bóveda y capturar el saldo inicial', grupo: 'Bóveda' },
  'boveda.retirar': { titulo: 'Retirar o pagar desde la bóveda (incluye compras y nómina)', grupo: 'Bóveda' },
  'boveda.traspasar': { titulo: 'Traspasar entre bóvedas', grupo: 'Bóveda' },
  'promociones.proponer': { titulo: 'Proponer promociones', grupo: 'Promociones' },
  'promociones.autorizar': { titulo: 'Autorizar promociones', grupo: 'Promociones' },
  'reportes.dueno': { titulo: 'Reportes del dueño', grupo: 'Reportes' },
  'reportes.recepcion': { titulo: 'Reportes de recepción', grupo: 'Reportes' },
  'reportes.propios': { titulo: 'Reportes propios', grupo: 'Reportes' },
  'usuarios.gestionar': { titulo: 'Dar de alta, baja y recuperar accesos', grupo: 'Usuarios' },
  'config.ver': { titulo: 'Ver configuración', grupo: 'Configuración' },
  'config.editar': { titulo: 'Editar configuración', grupo: 'Configuración' },
  'config.tipo_cambio': { titulo: 'Cambiar el tipo de cambio del dólar', grupo: 'Configuración' },
  'config.mercadopago': { titulo: 'Conectar Mercado Pago', grupo: 'Configuración' },
  'aprobaciones.resolver': { titulo: 'Aprobar o rechazar solicitudes', grupo: 'Aprobaciones' },
  'importacion.usar': { titulo: 'Importar citas y ventas desde Excel', grupo: 'Importación' },
  'auditoria.ver': { titulo: 'Ver bitácora de auditoría', grupo: 'Auditoría' },
} as const satisfies Record<string, { titulo: string; grupo: string }>;

export type Permiso = keyof typeof PERMISOS;
export const LISTA_PERMISOS = Object.keys(PERMISOS) as Permiso[];

export function esPermiso(valor: string): valor is Permiso {
  return Object.prototype.hasOwnProperty.call(PERMISOS, valor);
}

export const PERMISOS_POR_ROL: Record<Rol, readonly Permiso[]> = {
  DUENO: LISTA_PERMISOS,
  RECEPCION: [
    'agenda.ver',
    'agenda.ver_propia',
    'agenda.crear',
    'agenda.cancelar',
    'agenda.reprogramar',
    'clientes.ver',
    'clientes.editar',
    'caja.operar',
    'ventas.cobrar',
    'ventas.ver_detalle',
    'ventas.devolver_solicitar',
    'inventario.ver',
    'inventario.editar',
    'personal.ver',
    'personal.horarios',
    'personal.checar_otros',
    'promociones.proponer',
    'reportes.recepcion',
    'usuarios.gestionar',
    'config.ver',
    'importacion.usar',
  ],
  BARBERO: ['agenda.ver_propia', 'agenda.recibir_citas', 'atencion.operar'],
};

export function permisosEfectivos(
  rol: Rol,
  extras: readonly Permiso[],
  revocados: readonly Permiso[] = [],
): Set<Permiso> {
  const resultado = new Set<Permiso>([...PERMISOS_POR_ROL[rol], ...extras]);
  for (const permiso of revocados) resultado.delete(permiso);
  return resultado;
}

export function puede(permisos: ReadonlySet<Permiso> | readonly Permiso[], permiso: Permiso): boolean {
  return Array.isArray(permisos)
    ? (permisos as readonly Permiso[]).includes(permiso)
    : (permisos as ReadonlySet<Permiso>).has(permiso);
}
